/**
 * The scene subject is drawn from the domain, not bought as a model.
 *
 * A guarded industrial stand: an irregular boundary, two structures inside it,
 * and camera positions on the fence line. Everything the canvas renders is
 * generated from this one plan, so the geometry and the copy cannot drift apart.
 */

/** Boundary of the stand, clockwise, in world units on the ground plane. */
export const boundary: ReadonlyArray<readonly [number, number]> = [
  [-4.3, -3.1],
  [1.1, -3.5],
  [3.7, -2.4],
  [4.5, 0.4],
  [2.4, 3.3],
  [-2.5, 3.5],
  [-4.7, 1.1],
];

/** Structures on the stand: [centreX, centreZ, halfWidth, halfDepth, height]. */
export const structures: ReadonlyArray<readonly [number, number, number, number, number]> = [
  [-1.5, -0.6, 1.9, 1.1, 0.55],
  [2.1, 1.4, 0.9, 0.7, 0.34],
];

/** Camera posts, placed on the fence line where the boundary turns. */
export const cameraPosts: ReadonlyArray<readonly [number, number]> = [
  [-4.3, -3.1],
  [3.7, -2.4],
  [2.4, 3.3],
  [-4.7, 1.1],
];

export const gateAt: readonly [number, number] = [1.1, -3.5];

function isInside(x: number, z: number): boolean {
  let inside = false;

  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i, i += 1) {
    const [xi, zi] = boundary[i];
    const [xj, zj] = boundary[j];
    const crosses = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;

    if (crosses) {
      inside = !inside;
    }
  }

  return inside;
}

export interface PlanPoints {
  positions: Float32Array;
  count: number;
}

/**
 * Ground points inside the boundary, on a jittered lattice so the field reads
 * as a survey rather than as graph paper. `step` is the scene's whole quality
 * budget: raise it on a phone, lower it on a desktop.
 */
export function buildPlanPoints(step: number): PlanPoints {
  const positions: number[] = [];

  for (let x = -5; x <= 5; x += step) {
    for (let z = -4; z <= 4; z += step) {
      const jitterX = (Math.sin(x * 12.9898 + z * 78.233) * 43758.5453) % 1;
      const jitterZ = (Math.sin(x * 39.3468 + z * 11.135) * 24634.6345) % 1;
      const px = x + jitterX * step * 0.55;
      const pz = z + jitterZ * step * 0.55;

      if (!isInside(px, pz)) {
        continue;
      }

      positions.push(px, 0, pz);
    }
  }

  return {
    positions: new Float32Array(positions),
    count: positions.length / 3,
  };
}
